import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import EditorBadge from "@/components/EditorBadge";
import AdPlaceholder from "@/components/AdPlaceholder";
import { POSTS } from "@/data/posts";
import { BookOpen, Clock } from "lucide-react";

export default function BlogIndex() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "FinCalc Blog",
        description: "Honest, evergreen money math — real examples, no fluff.",
        blogPost: POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            description: p.description,
            datePublished: p.publishedAt,
            dateModified: p.updatedAt,
            url: `/blog/${p.slug}`,
        })),
    };

    return (
        <>
            <Seo
                title="FinCalc Blog — Honest Money Math & Plain-English Guides | FinCalc"
                description="Evergreen articles on mortgages, debt, taxes, retirement, and everyday money decisions. Real examples, no fluff."
                canonical="/blog"
                jsonLd={jsonLd}
            />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <span className="text-primary">Blog</span>
                </nav>
            </div>

            <section className="container-editorial pt-4 pb-10 anim-fade-up">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-3">
                    <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                    Blog · Editorial
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                    Honest money math, one story at a time.
                </h1>
                <p className="mt-4 max-w-2xl text-lg text-foreground/70 leading-relaxed">
                    Short, evergreen reads that pair with our calculators. No affiliate spam,
                    no &ldquo;10 secrets the banks don&apos;t want you to know&rdquo; clickbait
                    — just plain-English explanations of the numbers that shape real decisions.
                </p>
                <div className="mt-5"><EditorBadge /></div>
            </section>

            <div className="container-editorial mb-8">
                <AdPlaceholder type="leaderboard" dataTestId="ad-blog-leaderboard" />
            </div>

            <section className="container-editorial pb-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {POSTS.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/blog/${p.slug}`}
                            data-testid={`blog-card-${p.slug}`}
                            className="group bg-card rounded-xl border border-border p-6 hover:border-primary/30 hover:shadow-lg transition-all flex flex-col"
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] text-accent mb-3">{p.category}</div>
                            <h2 className="font-serif text-2xl text-primary leading-tight group-hover:text-secondary transition-colors">
                                {p.title}
                            </h2>
                            <p className="mt-3 text-sm text-foreground/75 leading-relaxed flex-1">
                                {p.description}
                            </p>
                            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-mono tabular">
                                <span className="flex items-center gap-1.5">
                                    <Clock className="w-3 h-3" strokeWidth={1.5} aria-hidden="true" />
                                    {p.readTime} min read
                                </span>
                                <span>{new Date(p.publishedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </>
    );
}
